import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  InputAdornment,
  Grid,
  Stack,
} from "@mui/material";
import dayjs from "dayjs";
import Input from "@mui/material/Input";
import FormControl from "@mui/material/FormControl";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  selectProductionParks,
  selectClientSupplyPlanPriceLimits,
  selectNordPoolPriceSteps,
} from "store/selectors";
import {
  fetchAllProductionPark,
  fetchClientSupplyPlanPriceLimits,
  fetchNordPoolPriceSteps,
} from "store/appSlice";
import { saveClientSupplyPlanPriceLimits } from "api/resources/clients";
import { getAuctionPriceLimitBounds } from "helpers/auctions";
import Select from "components/Select";
import ProductionParksSelect from "../ProductionParksSelect";

// Snap to nearest admin-defined step. On a tie, prefer the step closer to 0.
const snapToStep = (value: number, steps: number[]): number => {
  if (steps.length === 0) return value;
  let best = steps[0];
  let bestDist = Math.abs(value - best);
  for (let i = 1; i < steps.length; i++) {
    const step = steps[i];
    const dist = Math.abs(value - step);
    if (dist < bestDist) {
      best = step;
      bestDist = dist;
    } else if (dist === bestDist && Math.abs(step) < Math.abs(best)) {
      best = step;
    }
  }
  return best;
};

interface PriceLimitsProps {
  setSnackbar: (snackbar: { type: string; message: string }) => void;
  selectedAuction: number;
  currentRoleClientId: number;
}

interface PeriodPriceLimits {
  period: string;
  buy: number | "";
  sell: number | "";
}

const PriceLimits = ({
  setSnackbar,
  selectedAuction,
  currentRoleClientId,
}: PriceLimitsProps) => {
  const [periodPriceLimits, setPeriodPriceLimits] = useState([]);
  const [productionParkOptions, setProductionParkOptions] = useState([]);
  const [selectedProductionPark, setSelectedProductionPark] = useState<
    number | null
  >(null);
  const [selectedOtherProductionParks, setSelectedOtherProductionParks] =
    useState([]);
  const [valuesChanged, setValuesChanged] = useState(false);
  const [saveForMultiple, setSaveForMultiple] = useState(false);
  const [isProducer, setIsProducer] = useState(false);
  const [snapConfirm, setSnapConfirm] = useState<{
    rowIndex: number;
    field: "buy" | "sell";
    original: number;
    snapped: number;
    anchor: HTMLElement;
  } | null>(null);
  const [stepsAnchor, setStepsAnchor] = useState<HTMLElement | null>(null);

  const dispatch = useAppDispatch();
  const clientSupplyPlanPriceLimits = useAppSelector(
    selectClientSupplyPlanPriceLimits,
  );
  const productionParks = useAppSelector(selectProductionParks);
  const priceSteps = useAppSelector(selectNordPoolPriceSteps);
  const hasSteps = priceSteps.length > 0;
  const auctionBounds = getAuctionPriceLimitBounds(selectedAuction);

  useEffect(() => {
    dispatch(fetchAllProductionPark(0, 1000));
  }, [currentRoleClientId, selectedAuction, dispatch]);

  useEffect(() => {
    dispatch(fetchNordPoolPriceSteps());
  }, [dispatch]);

  const requestSnapConfirm = (
    rowIndex: number,
    field: "buy" | "sell",
    anchor: HTMLElement | null,
  ) => {
    if (!hasSteps || !anchor) return;
    const row = periodPriceLimits[rowIndex];
    if (!row) return;
    const current = row[field];
    if (current === "" || current === null || current === undefined) return;
    const numeric = Number(current);
    if (!Number.isFinite(numeric)) return;
    // Auction min/max are always valid snap targets, in addition to the
    // admin-defined price steps.
    const snapCandidates = Array.from(
      new Set([...priceSteps, auctionBounds.min, auctionBounds.max]),
    );
    const snapped = snapToStep(numeric, snapCandidates);
    if (snapped === numeric) return;

    setSnapConfirm({ rowIndex, field, original: numeric, snapped, anchor });
  };

  const confirmSnap = () => {
    if (!snapConfirm) return;
    const { rowIndex, field, snapped } = snapConfirm;
    const next = [...periodPriceLimits];
    next[rowIndex] = { ...next[rowIndex], [field]: snapped };
    setPeriodPriceLimits(next);
    setValuesChanged(true);
    setSnapConfirm(null);
  };

  useEffect(() => {
    dispatch(
      fetchClientSupplyPlanPriceLimits(
        currentRoleClientId,
        selectedAuction,
        selectedProductionPark,
      ),
    );
  }, [currentRoleClientId, selectedAuction, selectedProductionPark]);

  useEffect(() => {
    const options: React.SetStateAction<any[]> = [];

    if (productionParks) {
      if (productionParks.content.length > 0) {
        setIsProducer(true);
      }

      productionParks.content.map((park) => {
        options.push({ label: park.name, value: park.id });
      });

      setProductionParkOptions(options);

      if (productionParks.content.length === 1) {
        setSelectedProductionPark(productionParks.content[0].id);
      }
    }
  }, [productionParks]);

  const generateTable = () => {
    // Use today's date as a reference in Tallinn timezone
    const baseDate = dayjs().tz("Europe/Tallinn").startOf("day");

    const minutePeriods = Array.from({ length: 96 }, (_, i) => {
      const localTime = baseDate.add(i * 15, "minute"); // each 15-min interval
      return localTime;
    });

    const periodPriceLimitsCopy = minutePeriods.map((localTime) => {
      const utcTime = localTime.utc().format("HH:mm");

      const match = clientSupplyPlanPriceLimits?.limits.find(
        (sp) => sp.time === `${utcTime}:00`,
      );

      return {
        period: localTime, // Tallinn local
        buy: match?.buy ?? "",
        sell: match?.sell ?? "",
      };
    });

    setPeriodPriceLimits(periodPriceLimitsCopy);
  };

  useEffect(() => {
    generateTable();
  }, [clientSupplyPlanPriceLimits]);

  const handleSavePriceLimits = () => {
    const productionParksArray = [
      ...selectedOtherProductionParks.map((park) => park.value),
      selectedProductionPark,
    ];

    productionParksArray.forEach((park) => {
      const postData = {
        auctionId: selectedAuction,
        productionParkId: park,
        values: periodPriceLimits.map((priceLimit, priceLimitIndex) => {
          return {
            time: dayjs
              .tz(priceLimit.period, "Europe/Tallinn")
              .utc()
              .format("HH:mm"),
            buy: priceLimit.buy === "" ? null : priceLimit.buy ?? null,
            sell: priceLimit.sell === "" ? null : priceLimit.sell ?? null,
            ...(priceLimitIndex > 95 && { dayOffset: 1 }),
          };
        }),
      };

      saveClientSupplyPlanPriceLimits(currentRoleClientId, postData)
        .then(() => {
          setSnackbar({
            type: "success",
            message: "Price limits updated successfully",
          });
        })
        .catch(() => {
          setSnackbar({
            type: "error",
            message: "Error updating price limits",
          });
        });
    });
  };

  return (
    <Grid container columnSpacing={8}>
      <Grid item sm={6}>
        <Stack direction="column" spacing={2} sx={{ mb: 4 }}>
          <Box display="flex">
            {productionParkOptions.length > 0 && (
              <Select
                label="Select production park"
                id="productionPark"
                name="productionPark"
                minWidth={400}
                maxWidth={400}
                value={selectedProductionPark || ""}
                options={productionParkOptions}
                onChange={(e) => setSelectedProductionPark(e.target.value)}
              />
            )}
            {((isProducer && selectedProductionPark && valuesChanged) ||
              (!isProducer && productionParks && valuesChanged)) && (
              <Button
                variant="contained"
                onClick={() => handleSavePriceLimits()}
                sx={{ ml: 1 }}
              >
                Save
              </Button>
            )}
          </Box>

          {selectedProductionPark &&
            valuesChanged &&
            !saveForMultiple &&
            productionParkOptions.length > 1 && (
              <Box>
                <Button variant="text" onClick={() => setSaveForMultiple(true)}>
                  Save for multiple parks
                </Button>
              </Box>
            )}
          {selectedProductionPark && valuesChanged && saveForMultiple && (
            <ProductionParksSelect
              productionParks={productionParkOptions.filter(
                (option) => option.value !== selectedProductionPark,
              )}
              selectedProductionParks={selectedOtherProductionParks}
              setSelectedProductionParks={setSelectedOtherProductionParks}
              label="Apply limits to other parks"
              minWidth={400}
              maxWidth={400}
            />
          )}
        </Stack>
        {((isProducer &&
          productionParkOptions.length > 0 &&
          selectedProductionPark) ||
          (!isProducer && productionParks)) && (
          <Box sx={{ maxWidth: 490 }}>
            {hasSteps && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                  flexWrap: "wrap",
                }}
              >
                <InfoOutlinedIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Values change to {priceSteps.length} predefined step
                  {priceSteps.length === 1 ? "" : "s"} ({priceSteps[0]} –{" "}
                  {priceSteps[priceSteps.length - 1]} €).
                </Typography>
                <Button
                  size="small"
                  onClick={(e) => setStepsAnchor(e.currentTarget)}
                  sx={{
                    minWidth: 0,
                    p: 0,
                    textTransform: "none",
                    fontSize: "0.75rem",
                  }}
                >
                  View all
                </Button>
                <Popover
                  open={Boolean(stepsAnchor)}
                  anchorEl={stepsAnchor}
                  onClose={() => setStepsAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      maxWidth: 360,
                      maxHeight: 320,
                      overflowY: "auto",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                    }}
                  >
                    {priceSteps.map((s) => (
                      <Chip key={s} label={s.toString()} size="small" />
                    ))}
                  </Box>
                </Popover>
              </Box>
            )}
            <Popover
              open={Boolean(snapConfirm)}
              anchorEl={snapConfirm?.anchor ?? null}
              onClose={() => setSnapConfirm(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              disableAutoFocus
              disableEnforceFocus
            >
              <Box
                sx={{
                  p: 1.5,
                  maxWidth: 280,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography variant="body2">
                  <strong>{snapConfirm?.original}</strong> isn&apos;t a
                  predefined step. It will be changed to{" "}
                  <strong>{snapConfirm?.snapped}</strong>.
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={confirmSnap}
                  >
                    OK
                  </Button>
                </Box>
              </Box>
            </Popover>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#eeeeee" }}>
                  <TableRow>
                    <TableCell sx={{ minWidth: "180px" }}>
                      Period (Europe/Tallinn)
                    </TableCell>
                    <TableCell>Sell (€)</TableCell>
                    <TableCell>Buy (€)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periodPriceLimits.map((row, rowIndex) => (
                    <TableRow key={"Row-" + rowIndex}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography component="p">
                            {dayjs(row.period).format("HH:mm")}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: "100px" }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <FormControl variant="standard">
                            <Input
                              id="sell"
                              type="number"
                              size="small"
                              placeholder={String(auctionBounds.min)}
                              inputProps={{
                                min: auctionBounds.min,
                                max: auctionBounds.max,
                              }}
                              value={row.sell ?? ""}
                              sx={{
                                maxWidth: "110px",
                                backgroundColor:
                                  snapConfirm?.rowIndex === rowIndex &&
                                  snapConfirm?.field === "sell"
                                    ? "rgba(237, 108, 2, 0.16)"
                                    : "transparent",
                                transition: "background-color 200ms ease-out",
                              }}
                              startAdornment={
                                <InputAdornment position="start">
                                  <ChevronRightIcon />
                                </InputAdornment>
                              }
                              onChange={(e) => {
                                const periodPriceLimitsCopy: PeriodPriceLimits[] =
                                  [...periodPriceLimits];
                                setValuesChanged(true);

                                periodPriceLimitsCopy[rowIndex].sell =
                                  e.target.value === ""
                                    ? ""
                                    : Number(e.target.value);

                                setPeriodPriceLimits(periodPriceLimitsCopy);
                              }}
                              onBlur={(e) =>
                                requestSnapConfirm(
                                  rowIndex,
                                  "sell",
                                  e.currentTarget,
                                )
                              }
                            />
                          </FormControl>
                          <Box sx={{ width: 24, flexShrink: 0 }}>
                            {rowIndex === 0 &&
                              periodPriceLimits[0]?.sell !== "" && (
                                <Tooltip title="Apply to all">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const updated = periodPriceLimits.map(
                                        (r) => ({
                                          ...r,
                                          sell: periodPriceLimits[0].sell,
                                        }),
                                      );
                                      setPeriodPriceLimits(updated);
                                      setValuesChanged(true);
                                    }}
                                    sx={{ p: 0.25 }}
                                  >
                                    <KeyboardArrowDownIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: "100px" }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <FormControl variant="standard">
                            <Input
                              id="buy"
                              type="number"
                              size="small"
                              placeholder={String(auctionBounds.max)}
                              inputProps={{
                                min: auctionBounds.min,
                                max: auctionBounds.max,
                              }}
                              value={row.buy ?? ""}
                              sx={{
                                maxWidth: "110px",
                                backgroundColor:
                                  snapConfirm?.rowIndex === rowIndex &&
                                  snapConfirm?.field === "buy"
                                    ? "rgba(237, 108, 2, 0.16)"
                                    : "transparent",
                                transition: "background-color 200ms ease-out",
                              }}
                              startAdornment={
                                <InputAdornment position="start">
                                  <ChevronLeftIcon />
                                </InputAdornment>
                              }
                              onChange={(e) => {
                                const periodPriceLimitsCopy: PeriodPriceLimits[] =
                                  [...periodPriceLimits];
                                setValuesChanged(true);

                                periodPriceLimitsCopy[rowIndex].buy =
                                  e.target.value === ""
                                    ? ""
                                    : Number(e.target.value);

                                setPeriodPriceLimits(periodPriceLimitsCopy);
                              }}
                              onBlur={(e) =>
                                requestSnapConfirm(
                                  rowIndex,
                                  "buy",
                                  e.currentTarget,
                                )
                              }
                            />
                          </FormControl>
                          <Box sx={{ width: 24, flexShrink: 0 }}>
                            {rowIndex === 0 &&
                              periodPriceLimits[0]?.buy !== "" && (
                                <Tooltip title="Apply to all">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const updated = periodPriceLimits.map(
                                        (r) => ({
                                          ...r,
                                          buy: periodPriceLimits[0].buy,
                                        }),
                                      );
                                      setPeriodPriceLimits(updated);
                                      setValuesChanged(true);
                                    }}
                                    sx={{ p: 0.25 }}
                                  >
                                    <KeyboardArrowDownIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Grid>
      <Grid item sm={6}>
        <Typography component="p">
          Please enter your price limits for each period.
        </Typography>
        <Typography component="p" sx={{ mt: "1rem" }}>
          This table does not need to be updated daily, as the latest price
          limits will always be taken into account.
        </Typography>
      </Grid>
    </Grid>
  );
};

export default PriceLimits;
